import React, { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import CustomSnackbar from './CustomSnackbar';
import Select from 'react-select';
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { RefreshCw, Search } from 'react-feather';
import styled, { keyframes } from 'styled-components';
import translations from '../translations/addProductFamilyFormTranslation';
import { isTestEnv } from "../globals";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled.div`
  display: inline-block;
  animation: ${spin} 2s linear infinite;
`;

// סטיילינג מותאם אישית ל-react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '48px',
    border: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    borderRadius: 0,
    boxShadow: 'none',
    '&:hover': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.87)',
    },
    ...(state.isFocused && {
      borderBottom: '2px solid #1976d2',
      '&:hover': {
        borderBottom: '2px solid #1976d2',
      },
    }),
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '8px 0',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    '& > div': {
      padding: '8px',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '16px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.87)',
  }),
};
  
const AddProductFamilyForm = ({ rolesList, setProductFamilies, setMockDropdownData }) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const [open, setOpen] = useState(false);
  const [pfOptions, setPfOptions] = useState([]);
  const [rollsOptions, setRollsOptions] = useState([]);
  const [productFamilyOrg, setProductFamilyOrg] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productFamily, setProductFamily] = useState({ familyName: "", ft: "", orderApprovalRequired: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      const familyResponse = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/allProductFamilies`
      );
      setProductFamilyOrg([...familyResponse.data.filter(family => family.familyDes !== null && !family.infrastructures)]);
    })();
  }, []);

  useEffect(() => {
    const formattedFPOptions = productFamilyOrg.map(item => ({
      value: item._id,
      label: `${item.familyName} - ${item.familyDes}`,
      familyName: item.familyName,
      familyDes: item.familyDes
    }));
    setPfOptions(formattedFPOptions);

    const formattedRolesOptions = rolesList.map(item => ({
      value: item._id,
      label: item.Name
    }));
    setRollsOptions(formattedRolesOptions);
  }, [productFamilyOrg, rolesList]);

  // פונקציה מותאמת אישית לחיפוש משופר
  const customFilterFunction = (option, inputValue) => {
    if (!inputValue) return true;
    
    const searchTerm = inputValue.toLowerCase().trim();
    
    // חיפוש מדויק לפי קוד בתחילת הקלט
    if (option.familyName?.toLowerCase().startsWith(searchTerm)) {
      return true;
    }
    
    // חיפוש כללי
    return (
      option.familyName?.toLowerCase().includes(searchTerm) ||
      option.familyDes?.toLowerCase().includes(searchTerm) ||
      option.label?.toLowerCase().includes(searchTerm)
    );
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const productFamilyData = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/getDataFromPriority`
      );
    } catch (error) {
      isTestEnv && console.error('Error loading data:', error);
      openSnackbar(translations.LOADING_ERROR, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setProductFamily({ familyName: "", ft: "", orderApprovalRequired: false });
    setOpen(false);
  };

  const handleChange = (event) => {
    if (event) {
      const selectedFamily = productFamilyOrg.find(family => family._id === event.value);
      if (selectedFamily)
        setProductFamily((prevpf) => ({
          ...prevpf,
          _id: event.value,
          familyName: selectedFamily.familyName,
          familyDes: selectedFamily.familyDes,
          orderApprovalRequired: selectedFamily.orderApprovalRequired,
          approverRole: selectedFamily.approverRole,
          ft: selectedFamily.ft,
        }));
    } else {
      // אם event הוא null (הוסר הפריט), אפס את הנתונים
      setProductFamily({ familyName: "", ft: "", orderApprovalRequired: false });
    }
  };

  const handleRoleChange = (event) => {
    setProductFamily((prevr) => ({
      ...prevr,
      approverRole: event.value,
    }));
  };

  const handleApprovalChange = (event) => {
    setProductFamily((prevpf) => ({
      ...prevpf,
      orderApprovalRequired: event.target.checked,
      approverRole: event.target.checked ? prevpf.approverRole : "",
    }));
  };

  const handleSubmit = async () => {
    try {
      await execute("PATCH", `${process.env.REACT_APP_BACKEND_BASE_URL}/api/productFamily/updateProductFamily`, { _id: productFamily._id, infrastructures: true, orderApprovalRequired: productFamily.orderApprovalRequired, approverRole: productFamily.approverRole });
      openSnackbar(translations.ADD_SUCCESS, 'success');
      setProductFamilies((prev) => [...prev, { ...productFamily, approverRole: rolesList.find(r => r._id === productFamily.approverRole) }]);
      setMockDropdownData((prev) => ({ ...prev, family: [...prev.family, { ...productFamily, approverRole: rolesList.find(r => r._id === productFamily.approverRole) }] }));
      setProductFamily({ familyName: "", ft: "", orderApprovalRequired: false });
    } catch (error) {
      isTestEnv && console.error(error);
      openSnackbar(translations.ERROR_OCCURRED, 'error');
    }
    handleClose();
  };

  return (
    <div dir='rtl'>
      <div>
        <Button variant="contained" onClick={handleClickOpen}>
          {translations.ADD_PRODUCT_FAMILY}
        </Button>
      </div>
      <Button variant="text"
        onClick={handleRefresh}
        disabled={isLoading}
        style={{ color: isLoading ? "orange" : "GrayText" }}
      >
        {translations.REFRESH_DATA}
        <SpinningIcon>
          {isLoading && <RefreshCw
            size={16}
            className="spin-icon"
            style={{
              animation: isLoading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />}
        </SpinningIcon>
      </Button>
      <Dialog open={open} onClose={handleClose} dir='rtl'>
        <DialogTitle>{translations.ADD_PRODUCT_FAMILY}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translations.PLEASE_FILL_DETAILS}</DialogContentText>

          <div style={{ position: 'relative', marginTop: '16px', marginBottom: '8px' }}>
            <div style={{ 
              position: 'absolute', 
              left: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              zIndex: 1000,
              pointerEvents: 'none'
            }}>
              <Search size={20} color="rgba(0, 0, 0, 0.54)" />
            </div>
            <Select 
              options={pfOptions} 
              isSearchable 
              isClearable
              placeholder={`${translations.CHOOSE_PRODUCT_FAMILY} (הקלד קוד או שם לחיפוש)`}
              noOptionsMessage={() => translations.NO_OPTIONS} 
              onChange={handleChange}
              filterOption={customFilterFunction}
              value={pfOptions.find(option => option.value === productFamily._id) || null}
              styles={customSelectStyles}
              components={{
                DropdownIndicator: (props) => (
                  <div {...props.innerProps} style={{ padding: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                ),
              }}
            />
          </div>

          <TextField margin="dense" label={translations.PRODUCT_FAMILY_TYPE} value={productFamily.ft} InputProps={{ readOnly: true }} fullWidth variant="standard" />

          <FormControlLabel
            control={<Checkbox checked={productFamily.orderApprovalRequired} onChange={handleApprovalChange} inputProps={{ 'aria-label': translations.ORDER_APPROVAL_REQUIRED }} />}
            label={translations.ORDER_APPROVAL_REQUIRED}
          />

          {productFamily.orderApprovalRequired && (
            <Select 
              options={rollsOptions} 
              value={rollsOptions.find(r => r.value === productFamily.approverRole) || null}
              isSearchable 
              placeholder={translations.SELECT_APPROVAL_ROLE} 
              noOptionsMessage={() => translations.NO_OPTIONS} 
              onChange={handleRoleChange}
              styles={customSelectStyles}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{translations.CANCEL}</Button>
          <Button onClick={handleSubmit} disabled={!productFamily._id}>
            {translations.ADD}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} />
    </div>
  );
};

export default AddProductFamilyForm;